'use client'

import { useState, useEffect } from 'react'
import { X24, InfoCircle24, ArrowLeft24 } from '@vinted/monochrome-icons'
import { Button, Cell, Dialog, Image, Navigation, Text, Label, Divider, Icon } from '@vinted/web-ui'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { useAsset } from '@marketplace-web/shared/assets'
import {
  getShippingInstructions,
  transformShippingInstructionsResponse,
} from '@marketplace-web/conversation-shipping/conversation-shipping-data'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'

import GenericErrorModal from '../GenericErrorModal'

type Props = {
  isOpen: boolean
  onClose: () => void
}

const PackagingTipsModal = ({ isOpen, onClose }: Props) => {
  const translate = useTranslate('conversation.packaging_tips.modal')
  const shippingInstructionsTranslate = useTranslate(
    'conversation.view_delivery_instructions.modal',
  )
  const a11yTranslate = useTranslate('common.a11y.actions')
  const asset = useAsset('/assets/shipping')
  const { conversation } = useConversationContext()

  const transactionId = conversation?.transactionId

  const PHONE_HANDS = 'phone-hands-loader.svg'
  const PHONE_BOX = 'phone-box-packaging-paper.svg'
  const CARRIER_ICON_PLACEHOLDER = 'carrier-placeholder.svg'

  const [showShippingInstructionsView, setShowShippingInstructionsView] = useState(false)

  const {
    fetch: fetchShippingInstructions,
    transformedData: shippingInstructions,
    isLoading: isShippingInstructionsLoading,
    error: shippingInstructionsError,
  } = useFetch(getShippingInstructions, response => transformShippingInstructionsResponse(response))

  useEffect(() => {
    if (!isOpen) {
      setShowShippingInstructionsView(false)
    }
  }, [isOpen])

  const handleClose = () => {
    setShowShippingInstructionsView(false)
    onClose()
  }

  const openShippingInstructions = () => {
    if (transactionId) {
      fetchShippingInstructions(transactionId).then(() => {
        setShowShippingInstructionsView(true)
      })
    }
  }

  const handleBackFromInstructions = () => {
    setShowShippingInstructionsView(false)
  }

  const renderNavigation = () => (
    <>
      <Navigation
        body={translate('title')}
        left={
          <Button
            iconName={ArrowLeft24}
            styling="flat"
            onClick={handleClose}
            inline
            testId="packaging-tips-modal-back"
            aria={{ 'aria-label': a11yTranslate('back') }}
          />
        }
        right={
          <Button
            styling="flat"
            onClick={handleClose}
            iconName={X24}
            inline
            testId="packaging-tips-modal-close"
            aria={{ 'aria-label': a11yTranslate('dialog_close') }}
          />
        }
      />
      <Divider />
    </>
  )

  const renderFactoryResetSection = () => (
    <Cell>
      <Image src={asset(PHONE_HANDS)} scaling="contain" alt="" />
      <Text as="p" text={translate('factory_reset')} type="section-heading" />
      <Text as="p" text={translate('factory_reset_description')} type="subtitle" />
    </Cell>
  )

  const renderPackItSection = () => (
    <Cell>
      <Image src={asset(PHONE_BOX)} scaling="contain" alt="" />
      <Text as="p" text={translate('pack_it')} type="section-heading" />
      <Text as="p" text={translate('pack_it_description')} type="subtitle" html />
    </Cell>
  )

  const renderViewShippingInstructions = () => (
    <Cell>
      <Button
        onClick={openShippingInstructions}
        text={translate('actions.see_instructions')}
        testId="packaging-tips-modal-see-instructions"
        icon={<Icon name={InfoCircle24} />}
        theme="amplified"
        styling="flat"
      />
    </Cell>
  )

  const renderSubmit = () => (
    <div className="u-sticky u-bottom">
      <Cell>
        <Button
          onClick={handleClose}
          styling="filled"
          text={translate('actions.got_it')}
          testId="packaging-tips-modal-submit"
        />
      </Cell>
    </div>
  )

  const renderPackagingTipsContent = () => (
    <>
      {renderNavigation()}
      {renderFactoryResetSection()}
      {renderPackItSection()}
      {renderViewShippingInstructions()}
      {renderSubmit()}
    </>
  )

  const renderInstructionsNavigation = () => (
    <>
      <Navigation
        body={shippingInstructionsTranslate('title')}
        left={
          <Button
            iconName={ArrowLeft24}
            styling="flat"
            onClick={handleBackFromInstructions}
            inline
            testId="view-delivery-instructions-modal-go-back"
            aria={{ 'aria-label': a11yTranslate('back') }}
          />
        }
        right={
          <Button
            styling="flat"
            onClick={handleClose}
            iconName={X24}
            inline
            testId="view-delivery-instructions-modal-close"
            aria={{ 'aria-label': a11yTranslate('dialog_close') }}
          />
        }
      />
      <Divider />
    </>
  )

  const renderInstructionsCarrier = () => (
    <>
      <Label type="stacked" text={shippingInstructionsTranslate('label')} />
      <Cell
        prefix={
          <Image
            src={shippingInstructions?.carrier.iconUrl}
            size="large"
            styling="rounded"
            fallbackSrc={asset(CARRIER_ICON_PLACEHOLDER)}
          />
        }
        title={
          <Text as="h2" text={shippingInstructions?.carrier.name} type="title" theme="amplified" />
        }
        body={<Text as="span" text={shippingInstructionsTranslate('body')} />}
        testId="view-delivery-instructions-carrier"
      />
    </>
  )

  const renderShippingInstructions = () => (
    <>
      <Label type="stacked" text={shippingInstructionsTranslate('instructions_field_label')} />
      <Cell testId="view-delivery-instructions-instructions">
        <Text as="span" html text={shippingInstructions?.instructions} />
      </Cell>
    </>
  )

  const renderInstructionsSubmit = () => (
    <Cell>
      <Button
        onClick={handleClose}
        styling="filled"
        text={shippingInstructionsTranslate('actions.got_it')}
        testId="view-delivery-instructions-submit"
      />
    </Cell>
  )

  const renderShippingInstructionsContent = () => (
    <>
      {renderInstructionsNavigation()}
      <SeparatedList separator={<Divider />}>
        {renderInstructionsCarrier()}
        {renderShippingInstructions()}
        {renderInstructionsSubmit()}
      </SeparatedList>
    </>
  )

  if (isShippingInstructionsLoading)
    return (
      <Dialog show closeOnOverlay>
        <ContentLoader testId="view-delivery-instructions-modal-loader" />
      </Dialog>
    )

  if (shippingInstructionsError) return <GenericErrorModal isShown={isOpen} onClose={onClose} />

  return (
    <Dialog show={isOpen} closeOnOverlay testId="packaging-tips-modal">
      {showShippingInstructionsView && shippingInstructions
        ? renderShippingInstructionsContent()
        : renderPackagingTipsContent()}
    </Dialog>
  )
}

export default PackagingTipsModal
